import React, { useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileDown, FileSpreadsheet, AlertCircle, CheckCircle2, ArrowRight, X, Plus } from "lucide-react";
import api, { formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// Canonical order fields the import can populate. `required` drives the
// "you MUST map at least one column" validation in the mapping dialog.
const IMPORT_FIELDS = [
  { key: "customer_name",  label: "Customer name",  required: true,  hint: "Patient / recipient full name" },
  { key: "customer_phone", label: "Customer phone", required: true,  hint: "Digits are auto-normalized" },
  { key: "address",        label: "Address",        required: true,  hint: "Geocoded automatically" },
  { key: "items",          label: "Items",          required: true,  hint: "e.g. Amoxicillin 500mg|2;Ibuprofen|1" },
  { key: "pharmacy",       label: "Pharmacy",       required: false, hint: "Name or id — falls back to Default Pharmacy below" },
  { key: "priority",       label: "Priority",       required: false, hint: "normal / urgent / cold_chain / clinic / business — else uses Default Priority" },
  { key: "deliver_type",   label: "Deliver type",   required: false, hint: "same_day / next_day / otc / pickup — else uses Default Deliver Type" },
  { key: "order_number",   label: "Order #",        required: false, hint: "Optional — auto-generated if missing" },
  { key: "order_date",     label: "Order Create Date", required: false, hint: "Optional — YYYY-MM-DD or MM/DD/YYYY; defaults to today when blank" },
  { key: "primary_key",    label: "Primary Key (QR)", required: false, hint: "Optional — QR / barcode identifier printed on the package label" },
  { key: "notes",          label: "Notes",          required: false, hint: "Free-form delivery instructions" },
];

export default function BulkImportButton({ onImported }) {
  const { user } = useAuth();
  // Pharmacy Staff can only import into their own pharmacy — hide the
  // Pharmacy row + Default pharmacy dropdown; backend also enforces this.
  const isPharmacyStaff = user?.role === "pharmacy_staff";
  const fieldsToShow = React.useMemo(
    () => (isPharmacyStaff ? IMPORT_FIELDS.filter((f) => f.key !== "pharmacy") : IMPORT_FIELDS),
    [isPharmacyStaff],
  );
  const [open, setOpen] = useState(false);
  // Flow: choose file → inspect (columns + suggestion) → mapping dialog → upload
  const [file, setFile] = useState(null);
  const [inspecting, setInspecting] = useState(false);
  const [inspectData, setInspectData] = useState(null); // { columns, samples, suggested_mapping, saved_mapping }
  const [mapping, setMapping] = useState(null);         // { customer_name: [col], ... }
  const [pharmacies, setPharmacies] = useState([]);
  const [defaultPharmacyId, setDefaultPharmacyId] = useState("");
  const [defaultPriority, setDefaultPriority] = useState("normal");
  // Applied to every row that lacks a "deliver_type" cell (or when the file
  // has no such column at all). Same 4 values the New Order form uses.
  const [defaultDeliverType, setDefaultDeliverType] = useState("same_day");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setInspectData(null);
    setMapping(null);
    setResult(null);
    setDefaultPharmacyId("");
    setDefaultPriority("normal");
    setDefaultDeliverType("same_day");
    if (inputRef.current) inputRef.current.value = "";
  };

  // Fetch pharmacies once for the "Default pharmacy" dropdown (shown when
  // the user's file doesn't have a Pharmacy column).
  const ensurePharmacies = async () => {
    if (pharmacies.length) return;
    try {
      const { data } = await api.get("/pharmacies");
      setPharmacies(Array.isArray(data) ? data : []);
    } catch { /* non-fatal */ }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem("pr_token");
      const res = await fetch(`${API}/bulk/orders-template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to download template");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "orders_template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download template");
    }
  };

  // Step 1: inspect headers + seed the mapping form.
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setResult(null);
    setInspecting(true);
    try {
      await ensurePharmacies();
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/bulk/orders-inspect", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInspectData(data);
      // Seed mapping from saved-then-suggested, filtered to columns actually
      // present in THIS file so a stale saved mapping can't silently break.
      const available = new Set((data.columns || []).map(String));
      const filt = (cols) => (cols || []).filter((c) => available.has(String(c)));
      const saved = data.saved_mapping || {};
      const sug = data.suggested_mapping || {};
      const seeded = {};
      for (const f2 of fieldsToShow) {
        const s = filt(saved[f2.key]);
        seeded[f2.key] = s.length ? s : filt(sug[f2.key]);
      }
      setMapping(seeded);
    } catch (err) {
      toast.error(formatApiError(err));
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    } finally {
      setInspecting(false);
    }
  };

  // Client-side "still missing required" check for the primary CTA.
  const missingRequired = useMemo(() => {
    if (!mapping) return [];
    return fieldsToShow.filter((f) => f.required && (!mapping[f.key] || mapping[f.key].length === 0)).map((f) => f.label);
  }, [mapping, fieldsToShow]);

  const needsDefaultPharmacy = useMemo(() => {
    if (isPharmacyStaff) return false; // Backend pins to caller's pharmacy_id
    if (!mapping) return false;
    return !mapping.pharmacy || mapping.pharmacy.length === 0;
  }, [mapping, isPharmacyStaff]);

  // Step 2: submit the file + mapping.
  const upload = async () => {
    if (!file || !mapping) return;
    if (missingRequired.length) {
      toast.error(`Please map: ${missingRequired.join(", ")}`);
      return;
    }
    if (needsDefaultPharmacy && !defaultPharmacyId) {
      toast.error("Pick a Default Pharmacy (or map the Pharmacy column above)");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping", JSON.stringify(mapping));
      if (defaultPharmacyId) fd.append("default_pharmacy_id", defaultPharmacyId);
      if (defaultPriority) fd.append("default_priority", defaultPriority);
      if (defaultDeliverType) fd.append("default_deliver_type", defaultDeliverType);
      const { data } = await api.post("/bulk/orders-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.created > 0) toast.success(`Imported ${data.created} order(s)`);
      if (data.restricted > 0) {
        toast.warning(
          `${data.restricted} of ${data.created} order(s) placed On Hold — restricted ZIP (SRCI Admin approval required).`,
          { duration: 6000 }
        );
      }
      if (data.errors?.length) toast.warning(`${data.errors.length} row(s) had errors`);
      onImported?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full" data-testid="bulk-import-btn">
          <Upload className="h-4 w-4 mr-1.5" /> Excel Bulk Import
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-primary" /> Bulk import orders
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4" data-testid="bulk-import-dialog">
          {/* Step 1 — pick a file (always visible) */}
          {!inspectData && !result && (
            <>
              <Card className="p-4 bg-secondary/40 border-border rounded-xl">
                <div className="text-sm font-medium mb-1">Upload a CSV or Excel file</div>
                <p className="text-xs text-muted-foreground mb-2">
                  You&apos;ll pick which of your columns feed each order field on the next step —
                  works with any spreadsheet, not just our template.
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-primary hover:text-primary -ml-2"
                  onClick={downloadTemplate}
                  data-testid="bulk-import-template-btn"
                >
                  <FileDown className="h-3.5 w-3.5 mr-1.5" /> Download standard template
                </Button>
              </Card>

              <div>
                <label className="block">
                  <span className="text-sm font-medium">Choose file</span>
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".csv,.xlsx,.xlsm,text/csv"
                    onChange={onFile}
                    disabled={inspecting}
                    className="mt-2 block w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 file:cursor-pointer"
                    data-testid="bulk-import-file"
                  />
                </label>
                {inspecting && <p className="text-xs text-muted-foreground mt-2">Reading headers…</p>}
              </div>
            </>
          )}

          {/* Step 2 — mapping */}
          {inspectData && !result && (
            <>
              <Card className="p-3 flex items-center gap-2 text-sm">
                <FileSpreadsheet className="h-4 w-4 text-primary shrink-0" />
                <span className="flex-1 truncate"><b>{file?.name}</b> · {inspectData.columns.length} columns · {inspectData.samples?.length || 0} sample row{(inspectData.samples?.length || 0) === 1 ? "" : "s"}</span>
                <Button variant="ghost" size="sm" onClick={reset} className="h-8" data-testid="bulk-import-change-file">
                  <X className="h-3.5 w-3.5 mr-1" /> Change file
                </Button>
              </Card>

              <Card className="p-4 rounded-xl border-border">
                <div className="text-sm font-medium mb-1">Map your columns</div>
                <p className="text-xs text-muted-foreground mb-3">
                  Pick which spreadsheet column feeds each order field. Suggestions are pre-filled based on your headers; adjust as needed.
                </p>
                <div className="space-y-3">
                  {fieldsToShow.map((f) => {
                    const picks = mapping?.[f.key] || [];
                    // Allow up to 4 source columns per canonical field (e.g. First + Middle
                    // + Last + Suffix → Customer name, or Address 1 + Address 2 + City +
                    // State → Address). Backend joins them with a space.
                    const canAddMore = picks.length < 4;
                    const availableForNextPick = inspectData.columns.filter((c) => !picks.includes(c));
                    const setPickAt = (idx, val) => {
                      const next = picks.slice();
                      if (val === "__none__") next.splice(idx, 1);
                      else next[idx] = val;
                      setMapping({ ...(mapping || {}), [f.key]: next });
                    };
                    return (
                      <div key={f.key} className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-2 items-start" data-testid={`bulk-map-row-${f.key}`}>
                        <div>
                          <Label className="text-sm">
                            {f.label}
                            {f.required && <span className="text-destructive ml-0.5">*</span>}
                          </Label>
                          <p className="text-[11px] text-muted-foreground leading-snug">{f.hint}</p>
                        </div>
                        <div className="space-y-1.5">
                          {(picks.length === 0 ? [""] : picks).map((current, idx) => (
                            <div key={idx} className="flex items-center gap-1.5">
                              <Select
                                value={current || "__none__"}
                                onValueChange={(v) => setPickAt(idx, v)}
                              >
                                <SelectTrigger
                                  className="h-9"
                                  data-testid={idx === 0 ? `bulk-map-select-${f.key}` : `bulk-map-select-${f.key}-${idx}`}
                                >
                                  <SelectValue placeholder="— Not mapped —" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none__">— {idx === 0 ? "Not mapped" : "Remove"} —</SelectItem>
                                  {inspectData.columns
                                    .filter((c) => c === current || !picks.includes(c))
                                    .map((c) => (
                                      <SelectItem key={c} value={c}>{c}</SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                              {picks.length > 1 && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                                  onClick={() => setPickAt(idx, "__none__")}
                                  title="Remove this column"
                                  data-testid={`bulk-map-remove-${f.key}-${idx}`}
                                >
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          ))}
                          {picks.length >= 1 && canAddMore && availableForNextPick.length > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs text-primary hover:text-primary -ml-2"
                              onClick={() => setMapping({ ...(mapping || {}), [f.key]: [...picks, availableForNextPick[0]] })}
                              data-testid={`bulk-map-add-${f.key}`}
                            >
                              <Plus className="h-3 w-3 mr-1" /> Add another column
                            </Button>
                          )}
                          {picks.length > 1 && (
                            <p className="text-[11px] text-muted-foreground italic">Joined with a space: <span className="font-mono-num">{picks.join(" ")}</span></p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {inspectData.samples && inspectData.samples.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-border">
                    <div className="text-xs font-medium mb-1.5">Sample from your file</div>
                    <div className="overflow-x-auto">
                      <table className="text-[11px] font-mono-num w-full">
                        <thead className="bg-secondary/40">
                          <tr>
                            {inspectData.columns.map((c) => (
                              <th key={c} className="px-2 py-1 text-left font-medium border-b border-border truncate max-w-[140px]" title={c}>{c}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {inspectData.samples.slice(0, 3).map((row, i) => (
                            <tr key={i} className="border-b border-border/40">
                              {inspectData.columns.map((c) => (
                                <td key={c} className="px-2 py-1 text-muted-foreground truncate max-w-[140px]" title={String(row[c] || "")}>{String(row[c] || "")}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </Card>

              <Card className="p-4 rounded-xl border-border">
                <div className="text-sm font-medium mb-2">Defaults for this batch</div>
                <div className={`grid grid-cols-1 ${isPharmacyStaff ? "" : "sm:grid-cols-2"} gap-3`}>
                  {!isPharmacyStaff && (
                  <div>
                    <Label className="text-sm">
                      Default pharmacy
                      {needsDefaultPharmacy && <span className="text-destructive ml-0.5">*</span>}
                    </Label>
                    <Select
                      value={defaultPharmacyId || "__none__"}
                      onValueChange={(v) => setDefaultPharmacyId(v === "__none__" ? "" : v)}
                    >
                      <SelectTrigger className="h-9" data-testid="bulk-default-pharmacy">
                        <SelectValue placeholder="— None —" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {pharmacies.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Used when the Pharmacy column is empty (or not mapped).
                    </p>
                  </div>
                  )}
                  {isPharmacyStaff && (
                    <div className="text-xs text-muted-foreground bg-secondary/40 rounded-lg px-3 py-2 border border-border" data-testid="bulk-staff-pharmacy-notice">
                      Every imported order will be created for <b className="text-foreground">your pharmacy</b>. You can't import for a different pharmacy.
                    </div>
                  )}
                  <div>
                    <Label className="text-sm">Default priority</Label>
                    <Select value={defaultPriority} onValueChange={setDefaultPriority}>
                      <SelectTrigger className="h-9" data-testid="bulk-default-priority">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                        <SelectItem value="cold_chain">Cold chain</SelectItem>
                        <SelectItem value="clinic">Clinic</SelectItem>
                        <SelectItem value="business">Business</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Default deliver type</Label>
                    <Select value={defaultDeliverType} onValueChange={setDefaultDeliverType}>
                      <SelectTrigger className="h-9" data-testid="bulk-default-deliver-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="same_day">Same Day</SelectItem>
                        <SelectItem value="next_day">Next Day</SelectItem>
                        <SelectItem value="otc">OTC</SelectItem>
                        <SelectItem value="pickup">Pick Up</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Applied to rows that don&apos;t have a Deliver Type value (or when the file lacks that column).
                    </p>
                  </div>
                </div>
              </Card>

              {missingRequired.length > 0 && (
                <div className="text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2 flex items-center gap-2">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  Please map: {missingRequired.join(", ")}
                </div>
              )}
            </>
          )}

          {result && (
            <Card className="p-4 rounded-xl border-border" data-testid="bulk-import-result">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-accent" />
                <span><b className="font-mono-num">{result.created}</b> order(s) imported</span>
              </div>
              {result.restricted > 0 && (
                <div className="mt-2 flex items-start gap-2 text-sm text-[#7c2d12] bg-[#fff7ed] border border-[#f59e0b]/40 rounded-lg px-3 py-2">
                  <span className="text-base leading-none">⚑</span>
                  <span>
                    <b className="font-mono-num">{result.restricted}</b> of them landed in a
                    restricted ZIP and were placed <b>On Hold</b> pending SRCI Admin approval.
                    Look for the red flag in the Orders tab.
                  </span>
                </div>
              )}
              {result.errors?.length > 0 && (
                <div className="mt-3">
                  <div className="flex items-center gap-2 text-sm text-destructive mb-1.5">
                    <AlertCircle className="h-4 w-4" />
                    <span><b className="font-mono-num">{result.errors.length}</b> row(s) skipped</span>
                  </div>
                  <ul className="space-y-1 max-h-40 overflow-y-auto text-xs scrollbar-thin">
                    {result.errors.map((er, i) => (
                      <li key={i} className="font-mono-num text-muted-foreground">Row {er.row}: {er.message}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex gap-2 justify-end pt-3">
                <Button variant="outline" onClick={reset} data-testid="bulk-import-another">Import another</Button>
                <Button onClick={() => setOpen(false)} data-testid="bulk-import-close-result">Close</Button>
              </div>
            </Card>
          )}

          {inspectData && !result && (
            <div className="flex gap-2 justify-end pt-1">
              <Button variant="ghost" onClick={() => setOpen(false)} data-testid="bulk-import-cancel">Cancel</Button>
              <Button
                onClick={upload}
                disabled={busy || missingRequired.length > 0 || (needsDefaultPharmacy && !defaultPharmacyId)}
                className="rounded-full"
                data-testid="bulk-import-upload"
              >
                <ArrowRight className="h-4 w-4 mr-1.5" /> {busy ? "Importing…" : "Import orders"}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
